import React, { useState, lazy } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Users, ShieldCheck, MapPin, AlertTriangle, Clock, MessageSquare, CheckCircle, Navigation as NavigationIcon, ZoomIn, ZoomOut, Maximize2, Layers } from 'lucide-react';
import { motion } from 'framer-motion';
const liveReports = [{
  id: 1,
  type: 'student',
  reporter: 'Chioma A.',
  category: 'Safety Concern',
  subject: 'Broken gate lock at Emerald Heights',
  location: 'Unilag, Akoka',
  priority: 'high',
  status: 'open',
  time: '5 mins ago',
  coordinates: {
    lat: 6.5158,
    lng: 3.3895
  }
}, {
  id: 2,
  type: 'landlord',
  reporter: 'Mr. Okafor',
  category: 'Tenant Issue',
  subject: 'Noise complaint from neighboring rooms',
  location: 'Sunshine Lodge, Yaba',
  priority: 'medium',
  status: 'investigating',
  time: '23 mins ago',
  coordinates: {
    lat: 6.5027,
    lng: 3.3724
  }
}, {
  id: 3,
  type: 'student',
  reporter: 'David O.',
  category: 'Maintenance',
  subject: 'Water supply interrupted for 2 days',
  location: 'The Haven, Bariga',
  priority: 'high',
  status: 'open',
  time: '1 hour ago',
  coordinates: {
    lat: 6.5355,
    lng: 3.3889
  }
}, {
  id: 4,
  type: 'student',
  reporter: 'Sarah K.',
  category: 'Security',
  subject: 'Suspicious activity near hostel entrance',
  location: 'Student Village, LUTH',
  priority: 'urgent',
  status: 'open',
  time: '2 hours ago',
  coordinates: {
    lat: 6.4969,
    lng: 3.3774
  }
}];
const studentLocations = [{
  id: 1,
  name: 'Emerald Heights',
  students: 45,
  status: 'safe',
  lat: 6.5158,
  lng: 3.3895
}, {
  id: 2,
  name: 'Sunshine Lodge',
  students: 32,
  status: 'safe',
  lat: 6.5027,
  lng: 3.3724
}, {
  id: 3,
  name: 'The Haven',
  students: 28,
  status: 'warning',
  lat: 6.5355,
  lng: 3.3889
}, {
  id: 4,
  name: 'Student Village',
  students: 51,
  status: 'alert',
  lat: 6.4969,
  lng: 3.3774
}, {
  id: 5,
  name: 'Campus View',
  students: 38,
  status: 'safe',
  lat: 6.5244,
  lng: 3.3812
}];
export function UniversityDashboard() {
  const [selectedLocation, setSelectedLocation] = useState<(typeof studentLocations)[0] | null>(null);
  const [mapView, setMapView] = useState<'satellite' | 'roadmap'>('roadmap');
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'investigating':
        return 'warning';
      case 'open':
        return 'danger';
      default:
        return 'neutral';
    }
  };
  const getLocationStatusColor = (status: string) => {
    switch (status) {
      case 'safe':
        return 'bg-emerald-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'alert':
        return 'bg-red-500';
      default:
        return 'bg-slate-500';
    }
  };
  // Google Maps embed URL with markers
  const mapUrl = `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=6.5158,3.3895&zoom=13&maptype=${mapView}`;
  return <DashboardLayout userType="university" data-id="element-1297" data-mp="mpe5o8wijd1">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-1298" data-mp="oduvk7uhpjpg">
        <Card data-id="element-1299" data-mp="ocjnvwxxbrt2">
          <CardContent className="p-6" data-id="element-1300" data-mp="t4tc98je4jt8">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1301" data-mp="t5uy881y2qpq">
              Total Students Tracked
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1302" data-mp="31ngvvvepoj32">12,450</div>
            <div className="text-xs text-emerald-600 font-medium mt-1" data-id="element-1303" data-mp="2wm183vl5fygz">
              95% Off-campus coverage
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1304" data-mp="2mp4xnkbox29s">
          <CardContent className="p-6" data-id="element-1305" data-mp="2mpohx7otc2qz">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1306" data-mp="2mqpujglh8xat">
              Verified Properties
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1307" data-mp="3keq503kkl5u4">850</div>
            <div className="text-xs text-emerald-600 font-medium mt-1" data-id="element-1308" data-mp="3feve400sog92">
              +24 this month
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1309" data-mp="35o0qosbtbam1">
          <CardContent className="p-6" data-id="element-1310" data-mp="35oix2hs6v57o">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1311" data-mp="35nh9jdtzkq92">
              Active Ambassadors
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1312" data-mp="6zttyy7vx6jc">120</div>
            <div className="text-xs text-slate-400 mt-1" data-id="element-1313" data-mp="268vl52dqajm">Simba Fleet</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-100" data-id="element-1314" data-mp="ah9a348iz3i9">
          <CardContent className="p-6" data-id="element-1315" data-mp="egqc1noqtiio">
            <div className="text-sm text-red-600 mb-1" data-id="element-1316" data-mp="ehrzkrmy3xha">Open Reports</div>
            <div className="text-3xl font-bold text-red-700" data-id="element-1317" data-mp="jg37uxxldzxe">8</div>
            <div className="text-xs text-red-600 font-medium mt-1" data-id="element-1318" data-mp="offtxggn32yx">
              Requires attention
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-1319" data-mp="teafgq5zr5mq">
        {/* Google Maps */}
        <Card className="lg:col-span-2" data-id="element-1320" data-mp="xeb4t5ump18k">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-1321" data-mp="12749xuu0ebl6">
            <CardTitle data-id="element-1322" data-mp="1296xm87ovsmq">Live Safety Map - Lagos Area</CardTitle>
            <div className="flex gap-2" data-id="element-1323" data-mp="175fhfywu0a08">
              <button onClick={() => setMapView(mapView === 'roadmap' ? 'satellite' : 'roadmap')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" title="Toggle map view" data-id="element-1324" data-mp="1c50iqfzh4ug4">
                <Layers className="w-4 h-4" data-id="element-1325" data-mp="rdmutx2ez3g5" />
              </button>
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1326" data-mp="mifwl4rwyqym">
                <Maximize2 className="w-4 h-4" data-id="element-1327" data-mp="116u5nxfxbbnm" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="h-96 rounded-b-xl relative overflow-hidden p-0" data-id="element-1328" data-mp="wdhlbzia1r0o">
            {/* Google Maps Embed */}
            <iframe width="100%" height="100%" style={{
            border: 0
          }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={mapUrl} className="rounded-b-xl" data-id="element-1329" data-mp="1a8k07c53qqpm"></iframe>

            {/* Legend Overlay */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-10" data-id="element-1330" data-mp="3lwjk49cpsvfs">
              <div className="text-xs font-bold text-brand-dark mb-2" data-id="element-1331" data-mp="3gyzygodotgdt">
                Status Legend
              </div>
              <div className="space-y-1" data-id="element-1332" data-mp="21dzayt66cy8e">
                <div className="flex items-center gap-2 text-xs" data-id="element-1333" data-mp="21cxrc0b3f3f2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-1334" data-mp="1mpcjxu76qi1c"></div>
                  <span data-id="element-1335" data-mp="1mn8j5l4l1z5a">
                    Safe (
                    {studentLocations.filter(l => l.status === 'safe').length}
                    )
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs" data-id="element-1336" data-mp="1dqr6uekhfv7y">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1337" data-mp="1hrjg37tcu048"></div>
                  <span data-id="element-1338" data-mp="13r8qs97p0tl6">
                    Warning (
                    {studentLocations.filter(l => l.status === 'warning').length}
                    )
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs" data-id="element-1339" data-mp="3332vtn4jqalj">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1340" data-mp="3318fkm47z6ns"></div>
                  <span data-id="element-1341" data-mp="1pvygfga225fc">
                    Alert (
                    {studentLocations.filter(l => l.status === 'alert').length}
                    )
                  </span>
                </div>
              </div>
            </div>

            {/* Location Info Overlay */}
            <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-10" data-id="element-1342" data-mp="175qjjfwy8b7l">
              <div className="text-xs font-bold text-brand-dark mb-1" data-id="element-1343" data-mp="126m8skudm5f5">
                Active Monitoring
              </div>
              <div className="text-xs text-slate-600" data-id="element-1344" data-mp="y5u0bttbw4jx">
                Tracking{' '}
                {studentLocations.reduce((acc, loc) => acc + loc.students, 0)}{' '}
                students across {studentLocations.length} verified locations
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Details */}
        <Card data-id="element-1345" data-mp="2v40z8badfxva">
          <CardHeader data-id="element-1346" data-mp="2v4j7656ec0j1">
            <CardTitle data-id="element-1347" data-mp="2v2ybudc9cdk4">Location Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-id="element-1348" data-mp="3022k91rcy79e">
            {studentLocations.map(location => <div key={location.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => setSelectedLocation(location)} data-id="element-1349" data-mp="34tashugybmkz">
                <div className="flex justify-between items-start mb-2" data-id="element-1350" data-mp="2c6q3h0ny7mkl">
                  <div className="font-bold text-brand-dark text-sm" data-id="element-1351" data-mp="2h51br6ylhp0p">
                    {location.name}
                  </div>
                  <div className={`w-3 h-3 rounded-full ${getLocationStatusColor(location.status)}`} data-id="element-1352" data-mp="2m1rrtuaegkm4"></div>
                </div>
                <div className="flex justify-between items-center text-xs" data-id="element-1353" data-mp="1bso5x0e0xpb">
                  <span className="text-slate-600" data-id="element-1354" data-mp="5dwaxhsibopw">
                    {location.students} students
                  </span>
                  <span className="text-slate-500 capitalize" data-id="element-1355" data-mp="5irgate3o1fl">
                    {location.status}
                  </span>
                </div>
              </div>)}
          </CardContent>
        </Card>
      </div>

      {/* Live Reports Section */}
      <Card data-id="element-1356" data-mp="k85po1hoeiuz">
        <CardHeader className="flex flex-row items-center justify-between" data-id="element-1357" data-mp="k8oxgpdm3qva">
          <div data-id="element-1358" data-mp="3iq2p77we2560">
            <CardTitle data-id="element-1359" data-mp="3iqkoyei9f1io">Live Reports & Cases</CardTitle>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1360" data-mp="3is5n0e3s92la">
              Real-time reports from students and landlords
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1361" data-mp="3snz7qkrno2e5">
            <Button variant="outline" size="sm" data-id="element-1362" data-mp="3smxj1dhpvhdy">
              Filter
            </Button>
            <Button size="sm" data-id="element-1363" data-mp="1jinprusq9lm">New Report</Button>
          </div>
        </CardHeader>
        <div className="overflow-x-auto" data-id="element-1364" data-mp="17wyhblr9m4js">
          <table className="w-full text-sm text-left" data-id="element-1365" data-mp="180cgym8jnznj">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-1366" data-mp="1cw1harse1kkw">
              <tr data-id="element-1367" data-mp="2xe3sn6wmavi9">
                <th className="px-6 py-4" data-id="element-1368" data-mp="2xelr8a6r5ltc">Reporter</th>
                <th className="px-6 py-4" data-id="element-1369" data-mp="2sfiq30tw7zqn">Category</th>
                <th className="px-6 py-4" data-id="element-1370" data-mp="2si57rtahzvml">Subject</th>
                <th className="px-6 py-4" data-id="element-1371" data-mp="2nok53nxymdh2">Location</th>
                <th className="px-6 py-4" data-id="element-1372" data-mp="2nmza5x7qgsio">Priority</th>
                <th className="px-6 py-4" data-id="element-1373" data-mp="2ip7h2zvcr0cw">Status</th>
                <th className="px-6 py-4" data-id="element-1374" data-mp="2dr614nt83h5n">Time</th>
                <th className="px-6 py-4" data-id="element-1375" data-mp="2drxkn09jpofk">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1376" data-mp="29ohp6797pu4g">
              {liveReports.map(report => <tr key={report.id} className="hover:bg-slate-50" data-id="element-1377" data-mp="24r9706o2dq37">
                  <td className="px-6 py-4" data-id="element-1378" data-mp="1zrwkxo50on1o">
                    <div className="flex items-center gap-2" data-id="element-1379" data-mp="1zsy1270w3zq9">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${report.type === 'student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-1380" data-mp="2zcx11hjooaj">
                        {report.type === 'student' ? 'S' : 'L'}
                      </div>
                      <div data-id="element-1381" data-mp="1x91lxjll4adt">
                        <div className="font-medium text-brand-dark" data-id="element-1382" data-mp="1iiau4bjztbap">
                          {report.reporter}
                        </div>
                        <div className="text-xs text-slate-500 capitalize" data-id="element-1383" data-mp="1nhx9aqjdt1m5">
                          {report.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1384" data-mp="2g51eowmdzhyg">
                    <span className="text-slate-700 font-medium" data-id="element-1385" data-mp="2191yszt1cdir">
                      {report.category}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-1386" data-mp="265shluw85n7v">
                    <div className="max-w-xs" data-id="element-1387" data-mp="psvibjki7edq">
                      <p className="text-slate-900 font-medium truncate" data-id="element-1388" data-mp="ps29md1ziesy">
                        {report.subject}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1389" data-mp="38yo7ewdzawg5">
                    <div className="flex items-center gap-1 text-slate-600" data-id="element-1390" data-mp="341ejxma5guuy">
                      <MapPin className="w-3 h-3" data-id="element-1391" data-mp="343ilvyohnjsl" />
                      <span className="text-xs" data-id="element-1392" data-mp="3hzw2l5l77h60">{report.location}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1393" data-mp="3dzu1axjdkpr9">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${getPriorityColor(report.priority)}`} data-id="element-1394" data-mp="2q9hvptblr53u">
                      {report.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-1395" data-mp="2z3cos2zg9534">
                    <Badge variant={getStatusColor(report.status) as any} className="capitalize" data-id="element-1396" data-mp="2ubl5mdimi1n0">
                      {report.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4" data-id="element-1397" data-mp="22jwyq6im67bk">
                    <div className="flex items-center gap-1 text-slate-500 text-xs" data-id="element-1398" data-mp="2f9o37nynjzop">
                      <Clock className="w-3 h-3" data-id="element-1399" data-mp="2k8ha2xjfosfw" />
                      {report.time}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1400" data-mp="2p6j3ocehka5m">
                    <div className="flex gap-2" data-id="element-1401" data-mp="2p5hgj9k73x7j">
                      <Button variant="ghost" size="sm" className="text-brand-primary hover:text-brand-light hover:bg-brand-primary/10" data-id="element-1402" data-mp="2tykeruzx3uu2">
                        <MessageSquare className="w-4 h-4" data-id="element-1403" data-mp="32x5r38esqdme" />
                      </Button>
                      {report.status !== 'resolved' && <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-id="element-1404" data-mp="3cxoj4qgil0fe">
                          <CheckCircle className="w-4 h-4" data-id="element-1405" data-mp="1ijx3nabi7mc1" />
                        </Button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </DashboardLayout>;
}